import type { DepGraph, DepNode } from './procgen-doc.js';

/**
 * Read-only dependency view (section 6.5): output nodes laid out in columns
 * by stage (longest path), edges drawn left to right. Cycle and error nodes
 * are outlined. It never edits anything (the node graph editor module is
 * owned elsewhere).
 */

const COL_W = 176;
const NODE_W = 150;
const ROW_H = 30;
const NODE_H = 22;
const PAD = 8;

export function layoutDepGraph(graph: DepGraph): { nodes: (DepNode & { x: number; y: number })[]; width: number; height: number } {
  const byStage = new Map<number, DepNode[]>();
  for (const n of graph.nodes) {
    const list = byStage.get(n.stage) ?? [];
    list.push(n);
    byStage.set(n.stage, list);
  }
  const stages = [...byStage.keys()].sort((a, b) => a - b);
  const placed: (DepNode & { x: number; y: number })[] = [];
  let rows = 0;
  stages.forEach((stage, col) => {
    const list = byStage.get(stage)!.slice().sort((a, b) => a.id.localeCompare(b.id));
    rows = Math.max(rows, list.length);
    list.forEach((n, row) => placed.push({ ...n, x: PAD + col * COL_W, y: PAD + 16 + row * ROW_H }));
  });
  return { nodes: placed, width: PAD * 2 + Math.max(1, stages.length) * COL_W - (COL_W - NODE_W), height: PAD * 2 + 16 + Math.max(1, rows) * ROW_H };
}

export function DependencyView({ graph, onSelect }: { graph: DepGraph; onSelect?: (id: string) => void }) {
  if (graph.nodes.length === 0) return <div className="muted pcg-empty-note">No outputs yet.</div>;
  const { nodes, width, height } = layoutDepGraph(graph);
  const at = new Map(nodes.map((n) => [n.id, n]));
  const stages = [...new Set(nodes.map((n) => n.stage))].sort((a, b) => a - b);
  return (
    <div className="pcg-deps">
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Dependency stages">
        {stages.map((s, col) => (
          <text key={s} className="pcg-dep-stage" x={PAD + col * COL_W} y={PAD + 8}>
            stage {s}
          </text>
        ))}
        {graph.edges.map((e, i) => {
          const a = at.get(e.from);
          const b = at.get(e.to);
          if (!a || !b) return null;
          const x1 = a.x + NODE_W;
          const y1 = a.y + NODE_H / 2;
          const x2 = b.x;
          const y2 = b.y + NODE_H / 2;
          const mid = (x1 + x2) / 2;
          const cyc = a.cycle && b.cycle;
          return <path key={i} className={`pcg-dep-edge${cyc ? ' is-cycle' : ''}`} d={`M${x1},${y1} C${mid},${y1} ${mid},${y2} ${x2},${y2}`} />;
        })}
        {nodes.map((n) => (
          <g
            key={n.id}
            className={`pcg-dep-node is-${n.group}${n.error ? ' is-error' : ''}${n.cycle ? ' is-cycle' : ''}`}
            transform={`translate(${n.x},${n.y})`}
            onClick={() => onSelect?.(n.id)}
          >
            <title>{`${n.label}${n.cycle ? ' (in a dependency cycle)' : ''}${n.error ? ' (has errors)' : ''}`}</title>
            <rect width={NODE_W} height={NODE_H} rx={4} />
            <text x={8} y={NODE_H / 2 + 4}>
              {n.label.length > 22 ? `${n.label.slice(0, 21)}…` : n.label}
            </text>
          </g>
        ))}
      </svg>
      {!graph.fromEngine && <div className="muted pcg-deps-note">Computed from this file's inputs and expressions.</div>}
    </div>
  );
}
